import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, Ticket, Copy, Check, Users, Globe, Calendar, Power } from 'lucide-react';
import { adminApi } from '../lib/api';

const CouponsManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', discount_percent: 10, email: '', days_valid: 90, reason: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [copiedCode, setCopiedCode] = useState('');
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try { setItems(await adminApi.getCoupons()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setError(''); setSaving(true);
    try {
      const payload = {
        ...form,
        discount_percent: parseInt(form.discount_percent) || 10,
        days_valid: parseInt(form.days_valid) || 90,
        email: form.email.trim() || null,
      };
      await adminApi.createCoupon(payload);
      setForm({ code: '', discount_percent: 10, email: '', days_valid: 90, reason: '' });
      setShowForm(false);
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const toggle = async (c) => {
    await adminApi.updateCoupon(c.id, { active: !c.active });
    load();
  };

  const remove = async (c) => {
    if (window.confirm(`Supprimer le coupon ${c.code} ?`)) {
      await adminApi.deleteCoupon(c.id);
      load();
    }
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(''), 1500);
  };

  const filtered = items.filter(c => {
    if (filter === 'active') return c.active && !c.used;
    if (filter === 'used') return c.used;
    if (filter === 'expired') {
      try { return new Date(c.expires_at) < new Date(); } catch { return false; }
    }
    return true;
  });

  const stats = {
    total: items.length,
    active: items.filter(c => c.active && !c.used).length,
    used: items.filter(c => c.used).length,
    savings: items.filter(c => c.used).reduce((s, c) => s + (c.discount_percent || 0), 0),
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Coupons promotionnels</h3>
          <p className="text-sm text-slate-500">
            {stats.total} au total · {stats.active} actifs · {stats.used} utilisés
          </p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2 px-4 py-2 bg-[#F4A340] hover:bg-[#E89534] text-white font-semibold rounded-full">
          <Plus className="w-4 h-4" /> Nouveau coupon
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl p-6 border-2 border-[#F4A340]/30 space-y-3">
          <h4 className="font-bold text-slate-900">Nouveau coupon promotionnel</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Code (laisser vide pour auto)</label>
              <input placeholder="ex: SUMMER2026 (auto si vide)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none font-mono uppercase" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Réduction (%)</label>
              <input type="number" min="1" max="90" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Email cible (vide = tous les clients)</label>
              <input type="email" placeholder="client@example.com (optionnel)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Validité (jours)</label>
              <input type="number" min="1" value={form.days_valid} onChange={(e) => setForm({ ...form, days_valid: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />
            </div>
          </div>
          <input placeholder="Raison / Campagne (ex: Rentrée 2026)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none" />

          <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-xs text-slate-700">
            💡 Si vous saisissez un email, un mail avec le code sera envoyé automatiquement au client. Sinon, le coupon fonctionnera pour tous les clients qui l'utilisent.
          </div>

          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-full text-slate-600 hover:bg-slate-100">Annuler</button>
            <button onClick={submit} disabled={saving} className="flex items-center gap-2 px-5 py-2 bg-[#F4A340] hover:bg-[#E89534] disabled:bg-slate-200 text-white font-semibold rounded-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Créer
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'all', label: 'Tous' },
          { id: 'active', label: 'Actifs' },
          { id: 'used', label: 'Utilisés' },
          { id: 'expired', label: 'Expirés' }
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-4 py-1.5 rounded-full text-xs font-medium ${
              filter === f.id ? 'bg-[#F4A340] text-white' : 'bg-white border border-slate-200 hover:border-[#F4A340]'
            }`}
          >{f.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-[#F4A340]" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400">
          <Ticket className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>Aucun coupon</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => {
            const expired = new Date(c.expires_at) < new Date();
            return (
              <div key={c.id} className={`bg-white rounded-2xl border overflow-hidden ${
                c.used ? 'border-slate-200 opacity-60' : expired ? 'border-red-200' : c.active ? 'border-[#F4A340]/40' : 'border-slate-200'
              }`}>
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="text-2xl font-bold text-[#E89534]">-{c.discount_percent}%</div>
                        {c.used && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 font-semibold">Utilisé</span>}
                        {expired && !c.used && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">Expiré</span>}
                        {!c.active && !c.used && !expired && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 font-semibold">Inactif</span>}
                      </div>
                      <button onClick={() => copy(c.code)} className="group flex items-center gap-2 font-mono font-bold text-slate-900 bg-slate-50 hover:bg-orange-50 border border-slate-200 hover:border-[#F4A340] rounded-lg px-3 py-1.5 transition-colors w-full">
                        <Ticket className="w-3.5 h-3.5 text-[#E89534]" />
                        <span className="flex-1 text-left truncate">{c.code}</span>
                        {copiedCode === c.code ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 text-slate-400 group-hover:text-[#E89534]" />}
                      </button>
                    </div>
                  </div>

                  <div className="mt-3 text-xs text-slate-500 space-y-1">
                    <div className="flex items-center gap-1.5">
                      {c.email ? <><Users className="w-3 h-3" /> {c.email}</> : <><Globe className="w-3 h-3" /> Tous les clients</>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3 h-3" /> Expire le {new Date(c.expires_at).toLocaleDateString('fr-FR')}
                    </div>
                    {c.reason && <div className="italic pt-1">{c.reason}</div>}
                    {c.created_by && <div className="text-[10px] pt-1 text-slate-400">Créé par {c.created_by}</div>}
                  </div>

                  {!c.used && (
                    <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100">
                      <button onClick={() => toggle(c)} className="flex-1 flex items-center justify-center gap-1 text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold transition-colors">
                        <Power className="w-3 h-3" /> {c.active ? 'Désactiver' : 'Activer'}
                      </button>
                      <button onClick={() => remove(c)} className="px-3 py-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-600 transition-colors">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CouponsManagement;
